import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Database, Loader2, AlertCircle, ExternalLink, User2, History, RefreshCw, DollarSign } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UF_LIST = [
  'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA',
  'PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
];

const formatBRL = (val) => {
  if (val == null) return 'N/A';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
};

const BensChart = ({ bens }) => {
  if (!bens || bens.length === 0) return null;

  // Group by type
  const byType = {};
  bens.forEach(b => {
    const tipo = b.tipo || 'Outros';
    byType[tipo] = (byType[tipo] || 0) + (b.valor || 0);
  });

  const data = Object.entries(byType)
    .map(([name, value]) => ({ name: name.length > 25 ? name.slice(0, 22) + '...' : name, value, fullName: name }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  if (data.length === 0) return null;

  return (
    <div className="mt-4" data-testid="tse-bens-chart">
      <h4 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-[#2E7D32]" /> Bens Declarados por Tipo
      </h4>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 36)}>
        <BarChart data={data} layout="vertical" margin={{ left: 5, right: 15 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis
            type="number"
            tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
            tick={{ fontSize: 10, fill: '#94A3B8' }}
          />
          <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 10, fill: '#475569' }} />
          <Tooltip
            formatter={(value) => [formatBRL(value), 'Valor']}
            contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 11 }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
            {data.map((_, idx) => (
              <Cell key={idx} fill={idx === 0 ? '#1E3A5F' : idx < 3 ? '#2E7D32' : '#94A3B8'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const TseDataPanel = ({ candidateName, candidateState }) => {
  const [tseData, setTseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUf, setSelectedUf] = useState('');
  const [selectedYear, setSelectedYear] = useState('2024');
  const [searched, setSearched] = useState(false);

  const inferUf = useCallback(() => {
    if (!candidateState) return '';
    const stateMap = {
      'acre': 'AC', 'alagoas': 'AL', 'amazonas': 'AM', 'amapa': 'AP', 'bahia': 'BA',
      'ceara': 'CE', 'distrito federal': 'DF', 'espirito santo': 'ES', 'goias': 'GO',
      'maranhao': 'MA', 'minas gerais': 'MG', 'mato grosso do sul': 'MS', 'mato grosso': 'MT',
      'para': 'PA', 'paraiba': 'PB', 'pernambuco': 'PE', 'piaui': 'PI', 'parana': 'PR',
      'rio de janeiro': 'RJ', 'rio grande do norte': 'RN', 'rondonia': 'RO', 'roraima': 'RR',
      'rio grande do sul': 'RS', 'santa catarina': 'SC', 'sergipe': 'SE', 'sao paulo': 'SP',
      'tocantins': 'TO',
    };
    const lower = candidateState.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (lower.length === 2 && UF_LIST.includes(lower.toUpperCase())) return lower.toUpperCase();
    return stateMap[lower] || '';
  }, [candidateState]);

  useEffect(() => {
    const uf = inferUf();
    if (uf) {
      setSelectedUf(uf);
      // For state-level (governor), default to 2022; for municipal, 2024
      setSelectedYear('2022');
    }
  }, [inferUf]);

  // Auto-search once UF is inferred
  const [autoSearched, setAutoSearched] = useState(false);
  useEffect(() => {
    if (candidateName && selectedUf && !autoSearched) {
      setAutoSearched(true);
      handleSearch();
    }
  }, [selectedUf]);

  const handleSearch = async () => {
    if (!candidateName) return;
    setLoading(true);
    setError(null);
    setSearched(true);

    const year = parseInt(selectedYear);
    const state = selectedUf || 'BR';

    try {
      const resp = await axios.get(`${API}/tse/search`, {
        params: { name: candidateName, year, state },
        timeout: 30000,
      });
      if (resp.data.candidates && resp.data.candidates.length > 0) {
        setTseData(resp.data);
      } else {
        // Try other years
        const otherYears = [2024, 2022, 2020, 2018].filter(y => y !== year);
        let found = null;
        for (const y of otherYears) {
          try {
            const r2 = await axios.get(`${API}/tse/search`, {
              params: { name: candidateName, year: y, state },
              timeout: 20000,
            });
            if (r2.data.candidates && r2.data.candidates.length > 0) {
              found = r2.data;
              break;
            }
          } catch { /* continue */ }
        }
        if (found) {
          setTseData(found);
        } else {
          setError(`Nenhum resultado para "${candidateName}" ${state !== 'BR' ? `em ${state}` : ''}`);
        }
      }
    } catch (err) {
      setError('Erro ao consultar o TSE. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const candidates = tseData?.candidates || [];
  const firstWithBens = candidates.find(c => c.bens && c.bens.length > 0);

  return (
    <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="tse-data-panel">
      <div className="flex items-center gap-3 mb-4">
        <Database className="w-5 h-5 text-[#1E3A5F]" />
        <h3 className="text-lg font-bold text-[#0F1C2E]">Dados Oficiais TSE</h3>
      </div>

      {/* Search Controls */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={selectedUf} onValueChange={setSelectedUf}>
          <SelectTrigger className="w-[100px] h-9 text-xs border-[#E2E8F0]" data-testid="tse-uf-select">
            <SelectValue placeholder="UF" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="BR">Todos</SelectItem>
            {UF_LIST.map(uf => (
              <SelectItem key={uf} value={uf}>{uf}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-[90px] h-9 text-xs border-[#E2E8F0]" data-testid="tse-year-select">
            <SelectValue placeholder="Ano" />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2022, 2020, 2018].map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          size="sm"
          onClick={handleSearch}
          disabled={loading}
          data-testid="tse-search-btn"
          className="h-9 bg-[#1E3A5F] hover:bg-[#152B47] text-white text-xs"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1">Buscar</span>
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8 text-[#94A3B8]">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          <span className="text-sm">Consultando TSE...</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex items-center gap-3 p-4 bg-[#F4F6F8] rounded-md" data-testid="tse-not-found">
          <AlertCircle className="w-5 h-5 text-[#94A3B8] shrink-0" />
          <p className="text-sm text-[#475569]">{error}</p>
        </div>
      )}

      {/* Results */}
      {!loading && !error && candidates.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#94A3B8]">
              {tseData.total_found} registro(s) - Eleicao {tseData.year}
              {tseData.state && tseData.state !== 'BR' ? ` (${tseData.state})` : ''}
            </p>
            <Badge className="bg-[#E8F5E9] text-[#2E7D32] text-xs">{tseData.year}</Badge>
          </div>

          <div className="space-y-3">
            {candidates.slice(0, 5).map((c, idx) => (
              <div key={idx} className="p-4 border border-[#E2E8F0] rounded-md" data-testid={`tse-candidate-${idx}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#F4F6F8] rounded-full flex items-center justify-center shrink-0">
                    <User2 className="w-5 h-5 text-[#1E3A5F]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#0F1C2E] text-sm">{c.nome_completo || c.nome_urna}</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {c.cargo && <Badge variant="outline" className="text-[10px] border-[#94A3B8] text-[#475569]">{c.cargo}</Badge>}
                      {c.partido && <Badge variant="outline" className="text-[10px] border-[#1E3A5F] text-[#1E3A5F]">{c.partido}</Badge>}
                      {c.numero && <Badge variant="outline" className="text-[10px] border-[#94A3B8] text-[#475569]">N. {c.numero}</Badge>}
                      {c.situacao && (
                        <Badge className={`text-[10px] ${
                          c.situacao.toLowerCase().includes('deferido') || c.situacao.toLowerCase().includes('eleito')
                            ? 'bg-[#E8F5E9] text-[#2E7D32]'
                            : c.situacao.toLowerCase().includes('indeferido')
                              ? 'bg-[#FFEBEE] text-[#C62828]'
                              : 'bg-[#F4F6F8] text-[#475569]'
                        }`}>
                          {c.situacao}
                        </Badge>
                      )}
                    </div>
                    {c.total_bens != null && (
                      <p className="text-xs text-[#2E7D32] font-medium mt-2">
                        Total de bens: {formatBRL(c.total_bens)}
                      </p>
                    )}
                    {c.reeleicao && (
                      <div className="flex items-center gap-1 mt-1">
                        <History className="w-3 h-3 text-[#F57F17]" />
                        <span className="text-xs text-[#F57F17] font-medium">Reeleicao</span>
                      </div>
                    )}
                    {c.ficha_limpa_motivo && (
                      <div className="mt-1 p-1.5 bg-[#FFEBEE] rounded text-[10px] text-[#C62828]">
                        Motivo ficha limpa identificado
                      </div>
                    )}
                    {/* Eleições anteriores */}
                    {c.eleicoes_anteriores && c.eleicoes_anteriores.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-[#E2E8F0]">
                        <p className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-1">Eleições Anteriores</p>
                        <div className="space-y-1">
                          {c.eleicoes_anteriores.slice(0, 5).map((e, i) => (
                            <p key={i} className="text-xs text-[#475569]">
                              {e.ano} - {e.cargo} ({e.partido}) - {e.local} - <span className="font-medium">{e.resultado}</span>
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bens Chart */}
          {firstWithBens && <BensChart bens={firstWithBens.bens} />}
        </>
      )}

      {/* Not searched yet */}
      {!loading && !error && !searched && candidates.length === 0 && (
        <p className="text-sm text-[#94A3B8] text-center py-4">Selecione UF e ano, depois clique Buscar</p>
      )}

      <div className="mt-4 pt-3 border-t border-[#E2E8F0]">
        <a
          href="https://divulgacandcontas.tse.jus.br/divulga/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-xs text-[#1E3A5F] hover:underline"
          data-testid="tse-link"
        >
          <ExternalLink className="w-3 h-3" />
          Acessar DivulgaCandContas (TSE)
        </a>
      </div>
    </Card>
  );
};

export default TseDataPanel;
